/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractDrawingModule } from '@appCanvasCommon/core/AbstractDrawingModule';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { Subject } from 'rxjs';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { DestroyOptions, IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
/**
 * The AbstractAreaDrawer is an abstract class that provides a consistent boilerplate
 * for when you want to draw something on the screen using the FancyGraphics object.
 * This will always create a core container, a graphics object and a shutdownSubject
 * Derived classes can add additional things to it by adding elements to the `container`
 * or the appropriate layer as needed.
 */
export abstract class AbstractAreaDrawer extends AbstractDrawingModule {
	/**
	 * Container that holds the graphics object (and any future standard elements)
	 * @protected
	 */
	protected _container: FancyContainer;

	/**
	 * Graphics object for drawing
	 * @protected
	 */
	protected _graphics: FancyGraphics = new FancyGraphics();
	/**
	 * Cleans up any internal subscriptions when the drawer is destroyed
	 * @protected
	 */
	protected _shutdownSubject$: Subject<void> = new Subject<void>();
	constructor(parentApplication: AbstractApplication, layerPath: LayerPaths) {
		super(parentApplication);
		this._container = parentApplication.layerManager.addLayer(layerPath);
		parentApplication.layerManager.addChildToLayer(layerPath, this._graphics);
		this.destroyList.push(this._container);
	}

	/**
	 * Set the graphic area to visible and clear previous frame.
	 */
	public draw(...args: any[]): void {
		this._graphics.clear();
		this._container.visible = true;
	}

	/**
	 * Sets the position of the graphic drawer
	 * @param x The X position to set the container.
	 * @param y The Y position to set the container.
	 */
	public update(x: number, y: number): void {
		this._container.position.set(x, y);
	}

	/**
	 * Destroy all the things.
	 * @param options PIXI-compatible destroy options.
	 */
	public destroy(options: IDestroyOptions = DestroyOptions.includeChildren): void {
		this._shutdownSubject$.next();
		super.destroy(options);
		this._container = null;
		this._graphics = null;
	}

	/**
	 * Helper function which will clear the current drawn graphics and then hide the container.
	 * @protected
	 */
	protected hide(): void {
		this._graphics.clear();
		this._container.visible = false;
	}

	/**
	 * Helper function which will show the container
	 * @protected
	 */
	protected show(): void {
		this._container.visible = true;
	}
}
